import React, { forwardRef, memo, ForwardRefRenderFunction, ChangeEvent } from 'react'

import { isEmpty } from 'lodash'
import InputMask from 'react-input-mask'
import { useSelector } from 'react-redux'

import { FontSize, FontWeight } from 'types'
import ErrorMessage from 'components/Common/ErrorMessage'
import { PlacesType } from 'react-tooltip'

import ToolTipWithButton from 'components/Common/ToolTipWithButton'
import InfoText from 'components/Common/InfoText'

interface IInputFieldProps {
  name?: string
  label?: string
  mask?: string
  placeholder?: string
  rows?: string
  type?: string
  value?: string
  onChange?: any
  onChangeText?: any
  defaultValue?: any
  disabled?: boolean
  readonly?: boolean
  step?: string
  help?: string
  maxLength?: string | number
  autoFocus?: boolean
  hidden?: boolean
  className?: string
  isCheckBox?: boolean
  id?: any
  onMouseOut?: any
  onBlur?: any
  onFocus?: any
  errors?: any
  showPassword?: any
  onKeyPress?: any
  fontSize?: FontSize | ''
  placeholderFontSize?: FontSize | ''
  icon?: string
  labelFontSize?: FontSize
  labelFontWeight?: FontWeight
  toolTipText?: string
  toolTipWidth?: string
  toolTipPlace?: PlacesType
  toolTipClass?: string
  toolTipOpacity?: number
  toolTipBackgroundColor?: string
  infoText?: string
  index?: number
  onWheel?: (event: ChangeEvent<HTMLInputElement>) => void
  onClickIcon?: any
  rightIcon?: any
}

const InputField: ForwardRefRenderFunction<any, IInputFieldProps> = (
  {
    name,
    label,
    placeholder,
    type = 'text',
    onChange,
    value,
    defaultValue,
    disabled,
    readonly,
    maxLength,
    autoFocus,
    hidden,
    id,
    onMouseOut,
    className,
    onBlur,
    onFocus,
    errors,
    showPassword,
    mask = '',
    onKeyPress,
    fontSize = '',
    placeholderFontSize = '',
    labelFontSize,
    icon,
    labelFontWeight,
    toolTipText,
    toolTipWidth,
    toolTipPlace,
    toolTipClass,
    toolTipOpacity,
    toolTipBackgroundColor,
    infoText,
    index,
    onWheel,
    onClickIcon,
    rightIcon,
  },
  ref
) => {
  const _placeholderFontSize = `placeholder:!text-${
    isEmpty(placeholderFontSize) ? 'base' : placeholderFontSize
  }`
  const bgColor = useSelector((state: any) => state.common.inputFieldBgColor)
  const getClasses = () => {
    const _fontSize = fontSize ? `text-${fontSize}` : 'xs:text-base sm:text-xl'

    let classes = ` border border-grey-light ${bgColor} rounded-xl placeholder:text-grey-darkest1x font-primary outline-0
      ${_fontSize}  mt-1.5 block w-full px-3 py-2  font-bold placeholder:font-medium ${_placeholderFontSize}
      ${
        icon
          ? 'pl-12 pr-3 placeholder:!font-medium placeholder:!text-base hover:border hover:border-primary-brandDark !mt-0 text-black'
          : disabled
          ? 'cursor-not-allowed px-3 text-primary-disabled'
          : readonly
          ? 'cursor-not-allowed'
          : 'hover:border hover:border-primary-brandDark focus:ring focus:ring-primary-brandLight focus:border focus:border-primary-brandDark shadow-primary-brandDark px-3 text-black'
      }   ${className}`
    if (errors) {
      classes = `${classes} border border-primary-error hover:border hover:border-primary-error focus:border-primary-error focus:ring-0 focus:ring-transparent`
    } else {
      classes = `${classes}`
    }
    return classes
  }

  return (
    <div className={className}>
      <div className={`${hidden ? 'hidden' : ''}`}>
        {infoText && <InfoText index={index} infoText={infoText} />}
        <ToolTipWithButton
          label={label}
          toolTipText={toolTipText}
          toolTipPlace={toolTipPlace}
          toolTipWidth={toolTipWidth}
          labelFontSize={labelFontSize}
          fontWeight={labelFontWeight ?? 'font-medium'}
          toolTipClass={toolTipClass}
          toolTipOpacity={toolTipOpacity}
          toolTipBackgroundColor={toolTipBackgroundColor}
        />
        <div className={`${icon || rightIcon ? 'flex relative' : ''}`}>
          {icon && <img className="absolute top-4 left-5" src={icon} alt="icon" />}
          <InputMask
            onWheel={onWheel}
            aria-invalid={errors}
            autoFocus={autoFocus}
            className={getClasses()}
            data-testid={name}
            defaultValue={defaultValue}
            disabled={disabled}
            hidden={hidden}
            id={id}
            mask={mask}
            maxLength={maxLength as number}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyPress={onKeyPress}
            onMouseOut={onMouseOut}
            placeholder={placeholder ? placeholder : ''}
            readOnly={readonly}
            ref={ref}
            title={label}
            type={type !== 'password' ? type : showPassword ? 'text' : 'password'}
            value={value}
          />
          {rightIcon && (
            <span onClick={onClickIcon}>
              <img className="absolute top-6 right-10 cursor-pointer" src={rightIcon} alt="icon" />
            </span>
          )}
        </div>
      </div>
      {errors && <ErrorMessage id={name}>{errors.message}</ErrorMessage>}
    </div>
  )
}

export default memo(forwardRef(InputField))
